import { useState } from "react";
import { Check, CheckCircle2, ChevronDown, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, fmtDate } from "@/lib/api";
import { PageTitle, useApi, Skeleton, Stagger, Item, btnPrimary } from "@/components/portal/ui";
import { RichContent } from "@/components/RichText";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

function WeekStreak({ streak }) {
  return (
    <div className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100" data-testid="renungan-streak-card">
      <div className="flex items-center justify-between">
        <p className="font-bold text-slate-900 flex items-center gap-2"><Flame className="w-5 h-5 text-amber-700" /> <span data-testid="renungan-streak-count">{streak.streak}</span> hari beruntun</p>
        <span className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-3 py-1">Total {streak.total}</span>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1.5 text-center text-[10px] font-semibold text-slate-500">
        {streak.week.map((d, i) => (
          <div key={d.date}>
            <span className={`w-full aspect-square rounded-xl grid place-items-center mb-1 text-[10px] font-bold ${d.read ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`renungan-streak-day-${d.date}`}>{d.read ? <Check className="w-3.5 h-3.5" /> : fmtDate(d.date, { day: "2-digit" })}</span>
            {DAYS[i]}
          </div>
        ))}
      </div>
    </div>
  );
}

function Devotion({ d, onRead, featured }) {
  const [open, setOpen] = useState(featured);
  const [loading, setLoading] = useState(false);
  const read = async () => {
    setLoading(true);
    try {
      await onRead(d.id);
    } finally {
      setLoading(false);
    }
  };
  return (
    <article className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-testid={`devotion-${d.id}`}>
      {featured && (
        <div className="relative h-48 overflow-hidden">
          <img src={fileUrl(d.image_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
          <p className="absolute bottom-4 left-5 text-white/90 text-xs font-bold uppercase tracking-widest">{d.verse}</p>
        </div>
      )}
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 flex items-center gap-4" data-testid={`devotion-toggle-${d.id}`}>
        {!featured && <span className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 grid place-items-center font-black text-sm shrink-0">{fmtDate(d.date, { day: "2-digit" })}</span>}
        <div className="flex-1">
          <p className={`${featured ? "text-xl" : "text-sm"} font-extrabold text-slate-900 leading-tight`}>{d.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{fmtDate(d.date)} · {d.author}</p>
        </div>
        {d.read ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <div className="px-5 pb-6 -mt-1">
          <blockquote className="pl-4 border-l-4 border-blue-600 text-slate-600 italic text-sm leading-relaxed">"{d.verse_text}" — {d.verse}</blockquote>
          <RichContent html={d.content} className="mt-4 text-sm text-slate-600 leading-relaxed" />
          <button onClick={read} disabled={d.read || loading} data-testid={`devotion-read-${d.id}`} className={`${btnPrimary} w-full mt-6 ${d.read ? "!bg-emerald-500" : ""}`}>
            <Check className="w-4 h-4" /> {d.read ? "Sudah dibaca" : "Tandai Selesai Dibaca"}
          </button>
        </div>
      )}
    </article>
  );
}

export default function Renungan() {
  const { data, setData } = useApi("/portal/devotions");
  const onRead = async (id) => {
    try {
      const { data: streak } = await api.post(`/portal/devotions/${id}/read`);
      setData((p) => ({ streak, items: p.items.map((x) => (x.id === id ? { ...x, read: true } : x)) }));
      toast.success(`Streak ${streak.streak} hari! Tetap semangat`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="portal-renungan">
      <PageTitle eyebrow="Saat Teduh" title="Renungan" />
      <div className="px-5 mt-4 space-y-5">
        {!data ? <><Skeleton className="h-36" /><Skeleton className="h-72" /></> : (
          <Stagger className="space-y-5">
            <Item><WeekStreak streak={data.streak} /></Item>
            {data.items.length === 0 && <p className="text-sm text-slate-500">Belum ada renungan.</p>}
            {data.items.map((d, i) => (
              <Item key={d.id}>{i === 1 && <p className="px-1 mb-3 text-sm font-bold text-slate-900">Sebelumnya</p>}<Devotion d={d} featured={i === 0} onRead={onRead} /></Item>
            ))}
          </Stagger>
        )}
      </div>
    </div>
  );
}
